import { lazy, type ComponentType } from 'react';

export const CHUNK_RELOAD_KEY = 'avium:chunk-reload';

/**
 * React.lazy wrapper that recovers from stale-chunk failures after a deploy.
 *
 * When a browser holds an older index.html than the currently-deployed assets, a
 * lazy `import()` requests a chunk hash that no longer exists and 404s ("Failed
 * to fetch dynamically imported module"). That surfaced as a hard ErrorBoundary
 * crash on routes like /knowledge-graph during frequent staging deploys.
 *
 * On such a failure we reload the page ONCE — guarded by a sessionStorage flag so
 * we never loop — which pulls the fresh index.html + current chunk hashes. A
 * successful load clears the guard, so each distinct stale-chunk episode gets its
 * own single retry. If a reload still can't load the chunk (genuine outage), the
 * error falls through to the ErrorBoundary instead of looping.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  return lazy(async () => {
    try {
      const mod = await factory();
      try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch { /* private mode */ }
      return mod;
    } catch (err) {
      try {
        if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
          window.location.reload();
        }
      } catch { /* sessionStorage unavailable — fall through to the ErrorBoundary */ }
      throw err;
    }
  });
}
